import { useCallback, useEffect, useRef, useState } from 'react';
import { directSupabase } from '../lib/supabaseClient';
import type { Project } from '../types/project';
import { matchesWorkspaceStatusFilter, workspaceLifecycleStatus } from '../utils/workflowUtils';
import { loadWorkspaceSnapshots } from '../pages/project-workspace/workspaceDashboard.service';
import type { WorkspaceSnapshot } from '../pages/project-workspace/workspaceDashboard.model';
import { WORKSPACE_STAGE_LIST, type WorkspaceStage } from '../pages/project-workspace/workspaceStages';

export const WORKSPACE_PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
export const DEFAULT_WORKSPACE_PAGE_SIZE = 20;

/** Stage filter options: the same stages as the Board lanes (status covers Completed / Cancelled). */
export const WORKSPACE_STAGE_FILTERS: WorkspaceStage[] = WORKSPACE_STAGE_LIST
  .map((stage) => stage.id)
  .filter((stage) => stage !== 'completed' && stage !== 'cancelled');

const LIST_COLUMNS = [
  'id',
  'project_name',
  'customer_name',
  'customer_email',
  'customer_id',
  'lifecycle_ref',
  'notes',
  'supply_only',
  'project_status',
  'complete',
  'workflow_tasks',
  'status',
  'updated_at',
  'created_at',
  'completed_at',
  'cancelled_at',
  'status_changed_at',
].join(', ');

export type WorkspaceStatusFilter = 'all' | 'active' | 'completed' | 'cancelled';

export type WorkspaceListStats = {
  total: number;
  active: number;
  completed: number;
  cancelled: number;
};

function escapeIlike(value: string) {
  return value.replace(/[%_,()]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** `KSG0374` / `ksg-0374` → `KSG-0374`; a bare suffix like `0374` is left as-is. */
export function normalizeRefSearch(needle: string) {
  const match = needle.trim().match(/^([a-z]{2,4})\s*-?\s*(\d+)$/i);
  return match ? `${match[1].toUpperCase()}-${match[2]}` : needle.trim();
}

async function projectIdsForRefSearch(needle: string): Promise<string[]> {
  if (!/\d/.test(needle)) return [];
  const ref = normalizeRefSearch(needle);
  const { data, error } = await directSupabase
    .from('documents')
    .select('project_id')
    .not('project_id', 'is', null)
    .or(`lifecycle_ref.ilike.%${ref}%,ref_no.ilike.%${ref}%`)
    .limit(500);
  if (error) throw new Error(error.message);
  return [...new Set((data || []).map((row: { project_id: string }) => row.project_id).filter(Boolean))];
}

/** Returns the builder wrapped, since awaiting a Supabase builder directly would execute it. */
async function applySearch(query: any, search: string): Promise<{ query: any }> {
  const needle = escapeIlike(search);
  if (needle) {
    const ref = normalizeRefSearch(needle);
    const refProjectIds = await projectIdsForRefSearch(needle);
    const clauses = [
      `project_name.ilike.%${needle}%`,
      `customer_name.ilike.%${needle}%`,
      `customer_email.ilike.%${needle}%`,
      `notes.ilike.%${needle}%`,
      `lifecycle_ref.ilike.%${ref}%`,
    ];
    if (refProjectIds.length) clauses.push(`id.in.(${refProjectIds.join(',')})`);
    query = query.or(clauses.join(','));
  }
  return { query };
}

export function useWorkspacesList(options: {
  search: string;
  statusFilter: WorkspaceStatusFilter;
  stageFilter: 'all' | WorkspaceStage;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) {
  const { search, statusFilter, stageFilter, page, pageSize, enabled = true } = options;
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<WorkspaceListStats>({ total: 0, active: 0, completed: 0, cancelled: 0 });
  // Where each listed workspace is: the Board's answer, from its records
  const [snapshots, setSnapshots] = useState<Record<string, WorkspaceSnapshot>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const fetchStats = useCallback(async () => {
    const { data, error: statsError } = await directSupabase
      .from('projects')
      .select('project_status, complete, workflow_tasks');
    if (statsError) throw new Error(statsError.message);
    const rows = data || [];
    setStats({
      total: rows.length,
      active: rows.filter((row) => workspaceLifecycleStatus(row) === 'Active').length,
      completed: rows.filter((row) => workspaceLifecycleStatus(row) === 'Completed').length,
      cancelled: rows.filter((row) => workspaceLifecycleStatus(row) === 'Cancelled').length,
    });
  }, []);

  const fetchList = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const from = (page - 1) * pageSize;
      const needsScan = statusFilter !== 'all' || stageFilter !== 'all';

      let query = directSupabase
        .from('projects')
        .select(LIST_COLUMNS, needsScan ? undefined : { count: 'exact' })
        .order('updated_at', { ascending: false })
        .order('created_at', { ascending: false });

      ({ query } = await applySearch(query, search));
      if (id !== requestId.current) return;

      if (!needsScan) {
        query = query.range(from, from + pageSize - 1);
      } else {
        query = query.limit(5000);
      }

      const { data, error: listError, count } = await query;
      if (listError) throw new Error(listError.message);
      if (id !== requestId.current) return;

      let rows = (data || []) as unknown as Project[];
      if (statusFilter !== 'all') {
        rows = rows.filter((project) => matchesWorkspaceStatusFilter(project, statusFilter));
      }
      let found: Record<string, WorkspaceSnapshot> = {};
      if (stageFilter !== 'all') {
        found = await loadWorkspaceSnapshots(rows as any);
        if (id !== requestId.current) return;
        rows = rows.filter((project) => found[project.id]?.stage === stageFilter);
      }
      const pageRows = needsScan ? rows.slice(from, from + pageSize) : rows;
      if (stageFilter === 'all') {
        found = await loadWorkspaceSnapshots(pageRows as any);
        if (id !== requestId.current) return;
      }
      setSnapshots(found);
      setTotal(needsScan ? rows.length : count || 0);
      setProjects(pageRows);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch workspaces');
      setProjects([]);
      setTotal(0);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, stageFilter]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void fetchList();
  }, [enabled, fetchList]);

  useEffect(() => {
    if (!enabled) return;
    void fetchStats().catch((err) => {
      setError(err instanceof Error ? err.message : 'Failed to fetch workspace stats');
    });
  }, [enabled, fetchStats]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchList(), fetchStats()]);
  }, [fetchList, fetchStats]);

  const listMatching = useCallback(async (): Promise<{ rows: Project[]; snapshots: Record<string, WorkspaceSnapshot> }> => {
    let query = directSupabase
      .from('projects')
      .select(LIST_COLUMNS)
      .order('updated_at', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(5000);
    ({ query } = await applySearch(query, search));
    const { data, error: listError } = await query;
    if (listError) throw new Error(listError.message);
    let rows = (data || []) as unknown as Project[];
    if (statusFilter !== 'all') {
      rows = rows.filter((project) => matchesWorkspaceStatusFilter(project, statusFilter));
    }
    const found = await loadWorkspaceSnapshots(rows as any);
    if (stageFilter !== 'all') {
      rows = rows.filter((project) => found[project.id]?.stage === stageFilter);
    }
    return { rows, snapshots: found };
  }, [search, statusFilter, stageFilter]);

  const snapshotFor = useCallback(
    (project: Project): WorkspaceSnapshot | null => snapshots[project.id] || null,
    [snapshots],
  );

  return {
    projects,
    total,
    stats,
    loading,
    error,
    refresh,
    listMatching,
    snapshotFor,
  };
}
